import {
  ApiError,
  qualityPacksControllerResolveByCode,
  type ResolveWorkOrderByCodeResponseDto,
} from '@artic/api-client';
import { ScanQrCode, useScanQrCode } from '@artic/ui/scan-qr-code';
import { useNavigate } from '@tanstack/react-router';

import { expectData } from '../_shared/api';
import { usePartnerId } from '../inbox/use-partner-name';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Work Orders toolbar scan button. Opens the shared `@artic/ui` scanner and
 * decides where each input points: a scanned QR (a `work_order_id` UUID) routes
 * straight to its detail page, while a typed pin code (`work_order_code`) is
 * resolved to a work order via the backend first. Mirrors the Quality Packs
 * scan widget.
 */
export function ScanWorkOrderButton() {
  const navigate = useNavigate();
  const partnerId = usePartnerId();

  const goToWorkOrder = (workOrderId: string) =>
    navigate({ to: '/work-orders/$workOrderId', params: { workOrderId } });

  const resolveCodeAndNavigate = async (code: string) => {
    if (!partnerId) {
      throw new Error('No partner selected.');
    }
    let workOrderId: string;
    try {
      const res = await qualityPacksControllerResolveByCode(partnerId, { code });
      workOrderId = expectData<ResolveWorkOrderByCodeResponseDto>(res).work_order_id;
    } catch (error) {
      // Only a genuine not-found means a bad pin. Surface other failures
      // (network, server, validation) distinctly so they aren't mislabeled as
      // user error. `useScanQrCode` shows the message inline and keeps the
      // dialog open so the user can retry.
      if (error instanceof ApiError && error.status === 404) {
        throw new Error('Pin code not found');
      }
      throw new Error('Could not look up that pin code. Please try again.');
    }
    await goToWorkOrder(workOrderId);
  };

  const scanner = useScanQrCode({
    // The QR encodes a work_order_id UUID → navigate directly. Defensive
    // fallback: if a non-UUID is ever scanned, treat it as a pin code.
    onScan: (value) => (UUID_RE.test(value) ? goToWorkOrder(value) : resolveCodeAndNavigate(value)),
    onSubmitPinCode: resolveCodeAndNavigate,
  });

  return <ScanQrCode {...scanner} />;
}
